import { Card, CardContent } from "../ui/card";
import { CheckCircle, Pause, Phone, Play } from "lucide-react";
import { FormEmbed, FormEmbedSkeleton, useSavedContactForm } from "../../forms/form-embed";
import type { HeroOverlay, HeroOverlayStrength } from "./hero-overlay";
import { Input, PhoneInput } from "../ui/input";
import { MS_PER_SECOND, formatPhone, phoneHref } from "@servicepro/utils";
import { BOLD_CONTAINER_CLASS } from "../ui/bold-container";
import type { BaseComponentProps } from '../../types/puck';
import { BookingWidgetInline } from "../booking-widget/booking-widget";
import { Button } from "../ui/button";
import { FRAME_ON_MEDIA_CLASS } from "../../lib/color-role";
import { HeroFactsCard } from "./hero-facts-card";
import type { HeroFactsCardContent } from "./hero.business";
import { HeroLogo } from "./hero-logo";
import type { ImageMotion } from '../../lib/image-motion';
import type { ImagePosition } from '../../lib/image-position';
import React from "react";
import { SmartLink } from "../smart-link";
import { Textarea } from "../ui/textarea";
import { cn } from "../../lib/utils";
import { getWindowJobsiteConfig } from "../../types/runtime-config";
import { hasFactsCardContent } from "./hero.business";
import { heroOverlayGradient } from "./hero-overlay";
import { imageMotionClass } from '../../lib/image-motion';
import { imagePositionStyle } from '../../lib/image-position';
import { resolveOverlayDarkness } from "../../lib/overlay-darkness";
import { submitLead } from "../../forms/submit-lead";
import { useSlideshow } from "../../lib/use-slideshow";
import { useTurnstile } from "../../hooks/use-turnstile";

type HeroVariant = "clean" | "panel" | "proof" | "bold";

interface HeroLeadCaptureCardProps {
  variant: HeroVariant;
  hasTrackingPhone?: boolean;
  /** Submit button label. Falls back to the resolved business CTA (set by render()). */
  submitLabel: string;
  /** Canonical business slug — the custom form's photo uploads resolve on it. */
  businessSlug?: string;
}


/** One photo in the showcase hero's rotation. */
interface HeroPhoto {
  imageUrl: string;
  alt?: string;
  /** Where the photo keeps its focus when the screen crops it. */
  position?: ImagePosition;
}

interface HeroProps extends BaseComponentProps {
  headline: string;
  subheadline: string;
  ctaText: string;
  /** No url, no button: a page never links to itself. */
  ctaUrl?: string;
  secondaryCtaText?: string;
  secondaryCtaUrl?: string;
  trustIndicators?: { text: string }[];
  backgroundImage?: string;
  /**
   * Photos the showcase layout cycles through. Falls back to `backgroundImage` when empty,
   * so a showcase hero with one photo is a static full-bleed hero rather than a broken
   * carousel.
   */
  photos?: HeroPhoto[];
  /** Showcase layout only: whether the headline, subheadline and buttons sit over the photo. */
  showCopy?: "yes" | "no";
  /** Showcase layout only: dot controls for flipping between photos. */
  showSlideshowDots?: "yes" | "no";
  /** Showcase layout only: a pause/play control for holding a photo. */
  showSlideshowPause?: "yes" | "no";
  /** Showcase layout only: how long each photo holds before fading. */
  slideSeconds?: number;
  /** Where the background image keeps its focus when the screen crops it. */
  imagePosition?: ImagePosition;
  /** Slow zoom-and-pan on the hero photography, or none. */
  imageMotion?: ImageMotion;
  overlay?: HeroOverlay;
  /** How dark the overlay is, 0 to 100; 50 is the look heroes always had. */
  overlayDarkness?: number;
  /** Light/Medium/Strong, saved before the darkness slider; read only when overlayDarkness is unset. */
  overlayStrength?: HeroOverlayStrength;
  textColor?: "auto" | "light" | "dark";
  layout: "centered" | "split" | "showcase";
  variant?: HeroVariant;
  keywordBadge?: string;
  /** A featured logo under the buttons, such as "As seen on This Old House". */
  logoImage?: string;
  logoImageAlt?: string;
  logoImageColor?: "original" | "white";
  hasTrackingPhone?: boolean;
  /** Split-layout side card: "lead" (quick form), "booking" (request flow) or "facts" (the business's facts). */
  heroForm?: "lead" | "booking" | "facts";
  /** Booking-widget inputs (resolved from business metadata by hero.config). */
  bookingSlug?: string;
  bookingApiUrl?: string;
  businessName?: string;
  /** The facts card's content (resolved from business metadata by hero.config). */
  factsCard?: HeroFactsCardContent;
  /**
   * Where the business works, for the bold variant's label above the headline
   * ("Rochester · Greece · Webster"). Resolved from the business's towns by hero.config.
   */
  serviceAreaLabel?: string;
}

const
  /**
   * Scrim behind showcase copy.
   *
   * Not heroOverlayGradient(): those presets are tuned to cover a whole hero and bottom out at
   * 30% alpha, so a scrim that only covers part of the frame leaves a hard seam across the
   * photo. These reach full transparency, and follow the same overlay choice so the control
   * still means something in this layout.
   */
  getShowcaseScrimClass = (overlay: HeroProps["overlay"]): string => {
    switch (overlay) {
      case "light": {
        return "jobsite:from-white/90 jobsite:via-white/45 jobsite:to-transparent";
      }
      case "gradient":
      case "brand": {
        return "jobsite:from-primary/90 jobsite:via-primary/40 jobsite:to-transparent";
      }
      default: {
        return "jobsite:from-black/80 jobsite:via-black/35 jobsite:to-transparent";
      }
    }
  },

  resolveLightText = (
    textColor: HeroProps["textColor"],
    backgroundImage: string | undefined,
    overlay: HeroProps["overlay"],
  ): boolean => {
    if (textColor === "light") {
      return true;
    }
    if (textColor === "dark") {
      return false;
    }
    if ((backgroundImage ?? "") === "") {
      return true;
    }
    return overlay === "dark" || overlay === "gradient" || overlay === "brand";
  },

  /** The bold variant's side cards: the page's warm white on the dark band, an accent bar on top. */
  BOLD_CARD_CLASS =
    "jobsite:border-0 jobsite:border-t-[6px] jobsite:border-accent jobsite:bg-background jobsite:text-foreground jobsite:shadow-2xl",

  /** The facts card on every other variant: the same card, its bar in the brand colour, as the rest of the page is. */
  FACTS_CARD_CLASS =
    "jobsite:border-0 jobsite:border-t-[6px] jobsite:border-primary jobsite:bg-background jobsite:text-foreground jobsite:shadow-2xl",

  /**
   * The bold headline: capitals sized to the copy column, not the screen, so it
   * reads as a poster on a desktop and still wraps cleanly on a 375px phone.
   */
  BOLD_HEADLINE_CLASS =
    "jobsite:font-heading jobsite:uppercase jobsite:leading-[0.92] jobsite:tracking-[-0.02em] jobsite:text-balance jobsite:break-words jobsite:hyphens-auto",

  /** Up to this many characters ("Plumber in Boulder, CO") the bold headline is set poster-size. */
  BOLD_SHORT_HEADLINE_CHARS = 26,

  /** Up to this many ("Plumber and electrician in Boulder, CO") it is set one step down. */
  BOLD_MEDIUM_HEADLINE_CHARS = 44,

  /**
   * The bold headline's size: the shorter the line, the bigger the type, so the
   * trade and the town fill the band while a long line the owner wrote still
   * fits a phone. An editor's inline-editable element has no length to read; it
   * gets the smallest step, which always fits.
   * @param {React.ReactNode} headline The headline.
   * @returns {string} The size class.
   */
  boldHeadlineSizeClass = (headline: React.ReactNode): string => {
    const length = typeof headline === "string" ? headline.length : Number.POSITIVE_INFINITY;
    if (length <= BOLD_SHORT_HEADLINE_CHARS) {
      return "jobsite:text-[clamp(2.75rem,13.5cqi,7.25rem)]";
    }
    if (length <= BOLD_MEDIUM_HEADLINE_CHARS) {
      return "jobsite:text-[clamp(2.25rem,10.5cqi,5.75rem)]";
    }
    return "jobsite:text-[clamp(2.125rem,9.5cqi,4.75rem)]";
  },

  BOLD_PRIMARY_BUTTON_CLASS =
    "jobsite:h-14 jobsite:w-full jobsite:px-7 jobsite:text-base jobsite:shadow-none jobsite:sm:w-auto jobsite:bg-accent jobsite:text-accent-foreground jobsite:hover:bg-accent/90 jobsite:hover:shadow-none",

  BOLD_SECONDARY_BUTTON_CLASS =
    "jobsite:h-14 jobsite:w-full jobsite:px-7 jobsite:text-base jobsite:shadow-none jobsite:sm:w-auto jobsite:border-2 jobsite:bg-transparent",

  /**
   * Type colour on the bold band: the theme's on-primary colour on its own dark
   * band, plain white over a photo, whatever the theme's primary is.
   */
  boldToneClasses = (onPhoto: boolean): { text: string; body: string; outline: string } => {
    if (onPhoto) {
      return {
        body: "jobsite:text-white/85",
        outline: "jobsite:border-white/80 jobsite:text-white jobsite:hover:bg-white/10 jobsite:hover:text-white",
        text: "jobsite:text-white",
      };
    }
    return {
      body: "jobsite:text-primary-foreground/80",
      outline:
        "jobsite:border-primary-foreground/70 jobsite:text-primary-foreground jobsite:hover:bg-primary-foreground/10 jobsite:hover:text-primary-foreground",
      text: "jobsite:text-primary-foreground",
    };
  },

  /**
   * Whether a value is text with something in it.
   * @param {string | null | undefined} value The value.
   * @returns {boolean} True for a non-empty string.
   */
  hasText = (value: string | null | undefined): value is string => (value ?? "") !== "",

  HeroLeadCaptureCard = ({
    variant,
    hasTrackingPhone,
    submitLabel,
    businessSlug,
  }: HeroLeadCaptureCardProps) => {
    // A saved custom contact form takes over the card's field area (the same
    // Form every surface renders); absent form = the built-in quick form below.
    // A skeleton holds the spot while the read resolves, so a saved form never
    // Flashes the built-in fields first.
    const { loading: resolvingForm, data: customForm } = useSavedContactForm(true),
      [formState, setFormState] = React.useState<"idle" | "submitting" | "success" | "error">(
        "idle",
      ),
      [formData, setFormData] = React.useState({
        email: "",
        message: "",
        name: "",
        phone: "",
      }),

      {
        tokenRef: turnstileTokenRef,
        containerRef: turnstileContainerRef,
        containerClassName: turnstileClassName,
      } = useTurnstile(),

      handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData((prev) => ({
          ...prev,
          [e.target.name]: e.target.value,
        }));
      },
      handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formEl = e.currentTarget;
        if (!formEl.checkValidity()) {
          formEl.reportValidity();
          return;
        }

        const config = getWindowJobsiteConfig(),
          turnstileToken = turnstileTokenRef.current;
        if ((config?.turnstileSiteKey ?? "") !== "" && !turnstileToken) {
          setFormState("error");
          return;
        }

        setFormState("submitting");

        try {
          if (config?.apiUrl && config?.siteId) {
            const params = new URLSearchParams(window.location.search),
              isPreview = params.get("preview") === "true",
              sessionId = isPreview ? null : sessionStorage.getItem("sp_session_id") || null,

              result = await submitLead({
                apiUrl: config.apiUrl,
                backupApiUrl: config.backupApiUrl,
                businessName: config.business?.name,
                payload: {
                  ...formData,
                  sessionId,
                  source: "hero-form",
                  variant: config?.variant,
                  ...(turnstileToken ? { turnstileToken } : {}),
                },
                siteId: config.siteId,
              });
            if (result.status === "failed") {
              throw new Error("Lead submission failed");
            }
          }

          setFormState("success");
          setFormData({ email: "", message: "", name: "", phone: "" });
          window.setTimeout(() => {
            setFormState("idle");
          }, 5000);
        } catch {
          setFormState("error");
          window.setTimeout(() => {
            setFormState("idle");
          }, 3000);
        }
      };

    return (
      <Card
        className={cn(
          "jobsite:overflow-hidden jobsite:border jobsite:shadow-2xl",
          variant === "clean" && "jobsite:border-border/80 jobsite:bg-background",
          variant === "panel" &&
            "jobsite:border-white/15 jobsite:bg-background/94 jobsite:backdrop-blur-xl",
          variant === "proof" && "jobsite:border-primary/10 jobsite:bg-background/98",
          variant === "bold" && BOLD_CARD_CLASS,
        )}
      >
        <CardContent className="jobsite:p-6 jobsite:sm:p-8">
          <div className="jobsite:mb-6 jobsite:space-y-2">
            <p className="jobsite:text-xs jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:text-primary/70">
              Request service
            </p>
            <p className="jobsite:text-2xl jobsite:font-heading jobsite:text-card-foreground">
              Get pricing and availability
            </p>
            <p className="jobsite:text-sm jobsite:text-muted-foreground">
              Share a few details and we&apos;ll follow up quickly.
            </p>
          </div>

          {resolvingForm ? (
            <FormEmbedSkeleton />
          ) : (customForm ? (
            <FormEmbed
              data={customForm}
              submitLabel={submitLabel}
              source="hero-form"
              hasTrackingPhone={hasTrackingPhone}
              businessSlug={businessSlug}
              buttonClassName={cn(variant === "proof" && "jobsite:rounded-full")}
            />
          ) : (
          <form className="jobsite:space-y-4" onSubmit={handleSubmit}>
            <div className="jobsite:grid jobsite:gap-4">
              <Input
                type="text"
                name="name"
                autoComplete="name"
                required
                value={formData.name}
                onChange={handleChange}
                disabled={formState === "submitting"}
                placeholder="Your name"
                className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
              />
              <Input
                type="email"
                name="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                disabled={formState === "submitting"}
                placeholder="Email address"
                className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
              />
              <PhoneInput
                name="phone"
                required
                value={formData.phone}
                onValueChange={(phone) => {
                  setFormData((prev) => ({ ...prev, phone }));
                }}
                disabled={formState === "submitting"}
                placeholder="Phone number"
                className="jobsite:h-12 jobsite:border-2 jobsite:text-base"
              />
              <Textarea
                name="message"
                rows={4}
                value={formData.message}
                onChange={handleChange}
                disabled={formState === "submitting"}
                placeholder="Tell us about your project"
                className="jobsite:resize-none jobsite:border-2 jobsite:text-base"
              />
            </div>

            <div ref={turnstileContainerRef} className={turnstileClassName} />

            <Button
              type="submit"
              disabled={formState === "submitting"}
              className={cn(
                "jobsite:h-12 jobsite:w-full jobsite:font-semibold",
                variant === "proof" && "jobsite:rounded-full",
              )}
              size="lg"
            >
              {formState === "submitting" ? "Sending..." : submitLabel}
            </Button>

            {formState === "success" && (
              <div className="jobsite:rounded-2xl jobsite:border jobsite:border-emerald-200 jobsite:bg-emerald-50 jobsite:p-4 jobsite:text-sm jobsite:font-medium jobsite:text-emerald-700">
                Thank you. We&apos;ll be in touch soon.
              </div>
            )}

            {formState === "error" && (
              <div className="jobsite:rounded-2xl jobsite:border jobsite:border-destructive/20 jobsite:bg-destructive/10 jobsite:p-4 jobsite:text-sm jobsite:font-medium jobsite:text-destructive">
                Something went wrong. Please try again.
              </div>
            )}

            {hasTrackingPhone && (
              <p className="jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground/70">
                By providing your phone number, you consent to receive calls and text messages (SMS).
                You can unsubscribe at any time by replying STOP. Message and data rates may apply.
              </p>
            )}
          </form>
          ))}
        </CardContent>
      </Card>
    );
  },

  DEFAULT_SLIDE_SECONDS = 6,

  /**
   * Full-bleed photography that crossfades. Every slide stays mounted and opacity does the
   * work, so there is no layout shift and no reflow as photos load.
   */
  HeroSlideshow = ({
    photos,
    alt,
    paused,
    showDots,
    showPause,
    slideSeconds,
    motionClass,
  }: {
    photos: HeroPhoto[];
    alt: string;
    paused: boolean;
    showDots: boolean;
    showPause: boolean;
    slideSeconds: number;
    motionClass: string;
  }) => {
    // Reduced motion stops the rotation via the hook, holding the current frame; the
    // Dots still navigate manually.
    const { index, goTo, playing, setPlaying, prefersReducedMotion } = useSlideshow({
        count: photos.length,
        intervalMs: slideSeconds * MS_PER_SECOND,
        paused,
      }),
      // Nothing is moving under reduced motion, so a pause control would be a lie.
      showsPauseControl = showPause && photos.length > 1 && !prefersReducedMotion;

    return (
      <>
        <div className="jobsite:absolute jobsite:inset-0" aria-hidden="true">
          {photos.map((photo, slideIndex) => (
            <img
              key={`${photo.imageUrl}-${slideIndex}`}
              src={photo.imageUrl}
              alt={slideIndex === 0 ? alt : ""}
              crossOrigin="anonymous"
              // Only the first slide is part of LCP; the rest must not compete with it.
              fetchPriority={slideIndex === 0 ? "high" : "low"}
              loading={slideIndex === 0 ? "eager" : "lazy"}
              style={imagePositionStyle(photo.position)}
              className={cn(
                "jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-full jobsite:object-cover jobsite:object-center jobsite:transition-opacity jobsite:duration-1000 jobsite:ease-in-out",
                slideIndex === index ? "jobsite:opacity-100" : "jobsite:opacity-0",
                motionClass,
              )}
            />
          ))}
        </div>
        {showsPauseControl ? (
          // Bottom-right: clear of the copy block (bottom-left) and the dots
          // (bottom-centre). Quiet enough not to fight the photography.
          <button
            type="button"
            onClick={() => {
              setPlaying(!playing);
            }}
            aria-label={playing ? "Pause slideshow" : "Play slideshow"}
            // Lets the editor canvas run this click instead of selecting the
            // Section, so the control is testable where it's configured.
            data-sp-interactive=""
            // Z-20, above the copy block: it is z-10, full width, bottom
            // Anchored and later in the DOM, so at equal z it covers the
            // Controls and swallows their clicks.
            className="jobsite:absolute jobsite:bottom-4 jobsite:right-4 jobsite:z-20 jobsite:flex jobsite:h-8 jobsite:w-8 jobsite:items-center jobsite:justify-center jobsite:rounded-full jobsite:bg-black/25 jobsite:text-white/90 jobsite:backdrop-blur-sm jobsite:transition-colors jobsite:hover:bg-black/45 jobsite:hover:text-white"
          >
            {playing ? (
              <Pause className="jobsite:h-3.5 jobsite:w-3.5" />
            ) : (
              <Play className="jobsite:h-3.5 jobsite:w-3.5" />
            )}
          </button>
        ) : null}
        {showDots && photos.length > 1 ? (
          // The row spans the full width, so it stays click-through and only the
          // Dots themselves take pointer events.
          <div className="jobsite:pointer-events-none jobsite:absolute jobsite:inset-x-0 jobsite:bottom-4 jobsite:z-20 jobsite:flex jobsite:justify-center jobsite:gap-1">
            {photos.map((photo, dotIndex) => (
              <button
                key={`dot-${photo.imageUrl}-${dotIndex}`}
                type="button"
                onClick={() => {
                  goTo(dotIndex);
                }}
                aria-label={`Show photo ${dotIndex + 1} of ${photos.length}`}
                aria-current={dotIndex === index}
                data-sp-interactive=""
                className="jobsite:pointer-events-auto jobsite:flex jobsite:h-6 jobsite:w-6 jobsite:items-center jobsite:justify-center"
              >
                <span
                  className={cn(
                    "jobsite:h-2 jobsite:w-2 jobsite:rounded-full jobsite:transition-colors jobsite:duration-300",
                    dotIndex === index ? "jobsite:bg-white" : "jobsite:bg-white/45",
                  )}
                />
              </button>
            ))}
          </div>
        ) : null}
      </>
    );
  },

  Hero: React.FC<HeroProps> = ({
    headline,
    subheadline,
    ctaText,
    ctaUrl,
    secondaryCtaText,
    secondaryCtaUrl,
    trustIndicators,
    backgroundImage,
    photos,
    showCopy = "yes",
    showSlideshowDots = "no",
    showSlideshowPause = "no",
    imageMotion = "none",
    slideSeconds = DEFAULT_SLIDE_SECONDS,
    imagePosition = "center",
    overlay = "dark",
    overlayStrength,
    overlayDarkness,
    textColor = "auto",
    layout = "centered",
    variant = "clean",
    keywordBadge,
    logoImage,
    logoImageAlt,
    logoImageColor,
    hasTrackingPhone,
    heroForm = "lead",
    bookingSlug,
    bookingApiUrl,
    businessName,
    factsCard,
    serviceAreaLabel,
    id,
    className,
    puck,
  }: HeroProps & {
    puck?: { metadata?: { business?: { phone?: string; telephone?: string } } };
  }) => {
    const isBold = variant === "bold",
      metadataPhone = puck?.metadata?.business?.phone || puck?.metadata?.business?.telephone,
      resolvedSecondaryCtaText = metadataPhone
        ? `Call Us: ${formatPhone(metadataPhone)}`
        : secondaryCtaText,
      runtimeConfig = getWindowJobsiteConfig(),
      navIsTransparent =
        (runtimeConfig?.navigation?.style ?? runtimeConfig?.navigationStyle) === "transparent",
      resolvedSecondaryCtaUrl = metadataPhone
        ? phoneHref(metadataPhone)
        : secondaryCtaUrl,

      [showcasePaused, setShowcasePaused] = React.useState(false),
      // Showcase sits on its own photos, so they are what the text has to stay legible against.
      showcaseFirstPhoto = (photos || []).find((photo) => photo?.imageUrl)?.imageUrl,
      textBackdrop = layout === "showcase" ? showcaseFirstPhoto || backgroundImage : backgroundImage,
      lightText = resolveLightText(textColor, textBackdrop, overlay),
      darkness = resolveOverlayDarkness({ darkness: overlayDarkness, legacyStrength: overlayStrength }),
      validTrustIndicators = (trustIndicators || []).filter((item) => {
        if (typeof item.text === "string") {
          return item.text.trim();
        }
        return item.text;
      },
      ),
      panelSurfaceClass = cn(
        "jobsite:mx-auto jobsite:max-w-5xl jobsite:border jobsite:px-6 jobsite:py-8 jobsite:shadow-2xl jobsite:sm:px-8 jobsite:md:px-10 jobsite:md:py-10",
        lightText
          ? "jobsite:border-white/15 jobsite:bg-black/25 jobsite:backdrop-blur-xl"
          : "jobsite:border-border/80 jobsite:bg-background/94 jobsite:backdrop-blur-md",
      ),
      headlineTextClass = lightText
        ? "jobsite:text-white jobsite:[text-shadow:0_2px_18px_rgba(0,0,0,0.34)]"
        : "jobsite:text-foreground",
      bodyTextClass = lightText
        ? "jobsite:text-white/88 jobsite:[text-shadow:0_1px_12px_rgba(0,0,0,0.28)]"
        : "jobsite:text-muted-foreground",
      emphasisTextClass = lightText ? "jobsite:text-white" : "jobsite:text-foreground",
      contentWidthClass = variant === "proof" ? "jobsite:max-w-5xl" : "jobsite:max-w-6xl",
      primaryButtonClass = cn(
        "jobsite:w-full jobsite:sm:w-auto jobsite:px-8 jobsite:py-6 jobsite:text-base jobsite:font-semibold",
        variant === "proof" && "jobsite:shadow-lg",
        lightText
          ? "jobsite:bg-background jobsite:text-foreground jobsite:hover:bg-background/90"
          : "jobsite:bg-primary jobsite:text-primary-foreground jobsite:hover:bg-primary/90",
      ),
      secondaryButtonClass = cn(
        "jobsite:w-full jobsite:sm:w-auto jobsite:px-8 jobsite:py-6 jobsite:text-base jobsite:font-semibold",
        lightText
          ? "jobsite:border-white/40 jobsite:bg-black/10 jobsite:backdrop-blur-md jobsite:text-white jobsite:hover:bg-black/20"
          : "jobsite:border-foreground/15 jobsite:bg-transparent jobsite:text-foreground jobsite:hover:bg-foreground/5",
      ),
      surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const,
      // The bold variant is always a dark band, so over a photo its overlay is dark
      // Whatever the overlay field says: its type is light and has to stay legible.
      boldOverlay = isBold ? "dark" : overlay,
      boldTone = boldToneClasses(hasText(textBackdrop)),
      boldEyebrowText = (serviceAreaLabel ?? "") === "" ? keywordBadge : serviceAreaLabel,
      // Size first: a font size merged after a leading class drops the leading.
      boldHeadlineClass = cn(boldHeadlineSizeClass(headline), BOLD_HEADLINE_CLASS, boldTone.text),
      boldBodyClass = cn("jobsite:max-w-xl jobsite:text-base jobsite:leading-relaxed jobsite:text-pretty jobsite:sm:text-lg", boldTone.body),
      boldSecondaryButtonClass = cn(BOLD_SECONDARY_BUTTON_CLASS, boldTone.outline),
      primaryCallsPhone = (ctaUrl ?? "").startsWith("tel:"),
      secondaryCallsPhone = (resolvedSecondaryCtaUrl ?? "").startsWith("tel:"),
      /**
       * The bold label above the headline: where the business works, else its keyword badge.
       * @param {boolean} centered Whether the copy is centred.
       * @returns {React.ReactNode} The label, or null without text.
       */
      renderBoldEyebrow = (centered: boolean): React.ReactNode => {
        if ((boldEyebrowText ?? "") === "") {
          return null;
        }
        return (
          <div className={cn("jobsite:flex", centered && "jobsite:justify-center")}>
            <span
              className="jobsite:inline-flex jobsite:max-w-full jobsite:bg-accent jobsite:px-2.5 jobsite:py-1 jobsite:text-[11px] jobsite:font-bold jobsite:uppercase jobsite:leading-snug jobsite:tracking-[0.14em] jobsite:text-accent-foreground jobsite:sm:text-xs"
              style={surfaceRadiusStyle}
            >
              {boldEyebrowText}
            </span>
          </div>
        );
      },
      /**
       * The bold buttons: the accent primary and an outlined call button.
       * @param {boolean} centered Whether the copy is centred.
       * @returns {React.ReactNode} The button row.
       */
      renderBoldButtons = (centered: boolean): React.ReactNode => (
        <div className={cn("jobsite:flex jobsite:flex-col jobsite:gap-3 jobsite:sm:flex-row", centered && "jobsite:items-center jobsite:justify-center")}>
          {ctaUrl !== undefined && (
            <Button asChild size="lg" className={BOLD_PRIMARY_BUTTON_CLASS}>
              <SmartLink to={ctaUrl}>
                {primaryCallsPhone && <Phone aria-hidden="true" />}
                {ctaText}
              </SmartLink>
            </Button>
          )}
          {hasText(resolvedSecondaryCtaText) && hasText(resolvedSecondaryCtaUrl) && (
            <Button asChild variant="outline" size="lg" className={boldSecondaryButtonClass}>
              <SmartLink to={resolvedSecondaryCtaUrl}>
                {secondaryCallsPhone && <Phone aria-hidden="true" />}
                {resolvedSecondaryCtaText}
              </SmartLink>
            </Button>
          )}
        </div>
      ),
      // The bold variant states facts in its side card, never in default chips.
      renderTrustIndicators = (centered: boolean) => {
        if (validTrustIndicators.length === 0 || isBold) {
          return null;
        }

        return (
          <div
            className={cn(
              "jobsite:flex jobsite:max-w-3xl jobsite:flex-wrap jobsite:gap-x-6 jobsite:gap-y-3",
              centered ? "jobsite:mx-auto jobsite:justify-center" : "jobsite:justify-start",
            )}
          >
            {validTrustIndicators.map((indicator, idx) => (
              <div
                key={idx}
                className={cn(
                  "jobsite:inline-flex jobsite:items-center jobsite:gap-3 jobsite:py-1",
                  centered ? "jobsite:justify-center" : "jobsite:justify-start",
                )}
              >
                <span
                  className={cn(
                    "jobsite:flex jobsite:h-8 jobsite:w-8 jobsite:shrink-0 jobsite:items-center jobsite:justify-center jobsite:rounded-full",
                    lightText
                      ? "jobsite:border jobsite:border-white/12 jobsite:bg-white/10"
                      : (variant === "clean"
                        ? "jobsite:bg-muted"
                        : "jobsite:bg-primary/10"),
                  )}
                >
                  <CheckCircle
                    className={cn(
                      "jobsite:h-4 jobsite:w-4",
                      lightText ? "jobsite:text-primary-foreground" : "jobsite:text-primary",
                    )}
                  />
                </span>
                <span
                  className={cn(
                    "jobsite:text-sm jobsite:font-semibold jobsite:tracking-tight jobsite:sm:text-[0.96rem]",
                    emphasisTextClass,
                  )}
                >
                  {indicator.text}
                </span>
              </div>
            ))}
          </div>
        );
      };


    if (layout === "showcase") {
      const slides = (photos || []).filter((photo) => photo?.imageUrl),
        showcasePhotos =
          slides.length > 0 ? slides : (backgroundImage ? [{ imageUrl: backgroundImage }] : []),
        hasPhotos = showcasePhotos.length > 0,
        showsCopy = showCopy !== "no";

      return (
        // The pause surface is the COPY block below, not the whole section: on a
        // Full-screen hero the pointer is almost always inside the section, so a
        // Section-wide hover-pause read as "the slideshow is broken". Reading or
        // Tabbing through the copy still stills the background behind it;
        // Photography-only heroes never pause (reduced-motion still stops them).
        <section
          id={id}
          className={cn(
            // Phones stop short of full height so the next section peeks: a
            // Full-screen photo with nothing under it reads as the end of the
            // Page, and the peek is a better scroll cue than any chevron.
            "jobsite:relative jobsite:flex jobsite:min-h-[80svh] jobsite:md:min-h-[100svh] jobsite:overflow-hidden",
            // The frame tells its children they are over photography, so an
            // Element set to `auto` comes out legible without configuration.
            hasPhotos && FRAME_ON_MEDIA_CLASS,
            showsCopy ? "jobsite:items-end" : "jobsite:items-center",
            !hasPhotos && isBold && "jobsite:bg-primary",
            !hasPhotos && !isBold &&
              "jobsite:bg-linear-to-br jobsite:from-primary jobsite:via-primary jobsite:to-primary/90",
            className,
          )}
        >
          {hasPhotos && (
            <HeroSlideshow
              photos={showcasePhotos}
              alt={headline}
              paused={showcasePaused}
              showDots={showSlideshowDots === "yes"}
              showPause={showSlideshowPause === "yes"}
              slideSeconds={slideSeconds || DEFAULT_SLIDE_SECONDS}
              motionClass={imageMotionClass(imageMotion)}
            />
          )}

          {/* A short scrim at the top only, so a transparent nav stays legible on a bright sky. */}
          {hasPhotos && (
            <div
              className="jobsite:pointer-events-none jobsite:absolute jobsite:inset-x-0 jobsite:top-0 jobsite:h-32 jobsite:bg-linear-to-b jobsite:from-black/35 jobsite:to-transparent"
              aria-hidden="true"
            />
          )}

          {hasPhotos && showsCopy && variant !== "panel" && (
            <div
              className={cn(
                "jobsite:pointer-events-none jobsite:absolute jobsite:inset-x-0 jobsite:bottom-0 jobsite:h-2/3 jobsite:bg-linear-to-t",
                getShowcaseScrimClass(boldOverlay),
              )}
              aria-hidden="true"
            />
          )}

          {/*
            Hiding the copy must not cost the page its only h1 — a homepage whose hero is pure
            photography still needs a heading for search engines and screen readers.
          */}
          {!showsCopy && <h1 className="jobsite:sr-only">{headline}</h1>}

          {/*
            A fixed inset from the screen edge, not the shared centred container. On a wide
            monitor the container's auto margins pushed the copy hundreds of pixels inward, so
            it read as floating in the middle of the photo rather than anchored to a corner.
          */}
          {showsCopy && (
            <div
              // Keyboard only: tabbing through the hero CTAs shouldn't happen
              // Against a moving background. Hover deliberately does NOT pause —
              // On a full-screen hero the pointer is nearly always inside it, so
              // Hover-pause made the slideshow look broken.
              onFocusCapture={() => {
                setShowcasePaused(true);
              }}
              onBlurCapture={() => {
                setShowcasePaused(false);
              }}
              className="jobsite:relative jobsite:z-10 jobsite:w-full jobsite:px-6 jobsite:pb-14 jobsite:sm:px-10 jobsite:md:pb-20 jobsite:lg:px-16"
            >
              {isBold ? (
                <div className="jobsite:@container jobsite:max-w-4xl jobsite:space-y-5">
                  {renderBoldEyebrow(false)}
                  <h1 className={boldHeadlineClass}>{headline}</h1>
                  {subheadline && <p className={boldBodyClass}>{subheadline}</p>}
                  <div className="jobsite:pt-2">{renderBoldButtons(false)}</div>
                </div>
              ) : (
              <div
                className={cn(
                  "jobsite:max-w-3xl jobsite:space-y-5",
                  variant === "panel" && panelSurfaceClass,
                )}
                style={variant === "panel" ? surfaceRadiusStyle : undefined}
              >
                {keywordBadge && (
                  <span
                    className={cn(
                      "jobsite:inline-flex jobsite:px-4 jobsite:py-1.5 jobsite:text-xs jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:shadow-lg",
                      lightText
                        ? "jobsite:border jobsite:border-white/28 jobsite:bg-black/15 jobsite:backdrop-blur-md jobsite:text-white"
                        : "jobsite:border jobsite:border-accent/25 jobsite:bg-accent/15 jobsite:text-accent",
                    )}
                    style={surfaceRadiusStyle}
                  >
                    {keywordBadge}
                  </span>
                )}

                <h1
                  className={cn(
                    "jobsite:font-heading jobsite:text-4xl jobsite:leading-[1.1] jobsite:tracking-tight jobsite:text-balance jobsite:sm:text-5xl jobsite:md:text-6xl",
                    headlineTextClass,
                  )}
                >
                  {headline}
                </h1>
                {subheadline && (
                  <p
                    className={cn(
                      "jobsite:max-w-2xl jobsite:text-lg jobsite:leading-relaxed jobsite:sm:text-xl",
                      bodyTextClass,
                    )}
                  >
                    {subheadline}
                  </p>
                )}
                <div className="jobsite:flex jobsite:flex-col jobsite:gap-3 jobsite:sm:flex-row">
                  {ctaUrl !== undefined && (
                    <Button asChild size="lg" className={primaryButtonClass}>
                      <SmartLink to={ctaUrl}>{ctaText}</SmartLink>
                    </Button>
                  )}
                  {resolvedSecondaryCtaText && resolvedSecondaryCtaUrl && (
                    <Button asChild variant="outline" size="lg" className={secondaryButtonClass}>
                      <SmartLink to={resolvedSecondaryCtaUrl}>{resolvedSecondaryCtaText}</SmartLink>
                    </Button>
                  )}
                </div>

                {/* Proof is the variant that leads with credentials, here as everywhere else. */}
                {variant === "proof" && renderTrustIndicators(false)}
                <HeroLogo src={logoImage} alt={logoImageAlt} color={logoImageColor} centered={false} />
              </div>
              )}
            </div>
          )}
        </section>
      );
    }

    if (layout === "centered") {
      return (
        <section
          id={id}
          className={cn(
            "jobsite:relative jobsite:overflow-hidden jobsite:pb-20 jobsite:md:pb-32",
            backgroundImage && FRAME_ON_MEDIA_CLASS,
            navIsTransparent
              ? "jobsite:pt-36 jobsite:md:pt-48"
              : "jobsite:pt-20 jobsite:md:pt-32",
            !hasText(backgroundImage) && isBold && "jobsite:bg-primary",
            !hasText(backgroundImage) && !isBold &&
              "jobsite:bg-linear-to-br jobsite:from-primary jobsite:via-primary jobsite:to-primary/90",
            className,
          )}
        >
          {backgroundImage && (
            <img
              src={backgroundImage}
              alt=""
              crossOrigin="anonymous"
              fetchPriority="high"
              style={imagePositionStyle(imagePosition)}
              className={cn(
                "jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-full jobsite:object-cover jobsite:object-center",
                imageMotionClass(imageMotion),
              )}
              aria-hidden="true"
            />
          )}
          {backgroundImage && (
            <div
              className="jobsite:absolute jobsite:inset-0"
              style={{ backgroundImage: heroOverlayGradient(boldOverlay, darkness) }}
              aria-hidden="true"
            />
          )}
          {navIsTransparent && backgroundImage && (
            <div
              className="jobsite:pointer-events-none jobsite:absolute jobsite:inset-x-0 jobsite:top-0 jobsite:h-32 jobsite:bg-linear-to-b jobsite:from-black/35 jobsite:to-transparent"
              aria-hidden="true"
            />
          )}

          {isBold ? (
            <div className={cn(BOLD_CONTAINER_CLASS, "jobsite:relative jobsite:z-10 jobsite:text-center")}>
              <div className="jobsite:@container jobsite:mx-auto jobsite:max-w-5xl jobsite:space-y-6">
                {renderBoldEyebrow(true)}
                <h1 className={cn("jobsite:mx-auto", boldHeadlineClass)}>{headline}</h1>
                {subheadline && <p className={cn("jobsite:mx-auto", boldBodyClass)}>{subheadline}</p>}
                <div className="jobsite:pt-2">{renderBoldButtons(true)}</div>
              </div>
            </div>
          ) : (
          <div
            className={cn(
              "jobsite:container jobsite:relative jobsite:z-10 jobsite:mx-auto jobsite:px-4 jobsite:text-center",
              contentWidthClass,
            )}
          >
            <div className={cn(variant === "panel" && panelSurfaceClass)}>
              <div className="jobsite:space-y-6">
                {keywordBadge && (
                  <div className="jobsite:flex jobsite:justify-center">
                    <span
                      className={cn(
                        "jobsite:inline-flex jobsite:px-4 jobsite:py-1.5 jobsite:text-xs jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:shadow-lg",
                        lightText
                          ? "jobsite:border jobsite:border-white/28 jobsite:bg-black/15 jobsite:backdrop-blur-md jobsite:text-white"
                          : "jobsite:border jobsite:border-accent/25 jobsite:bg-accent/15 jobsite:text-accent",
                      )}
                      style={surfaceRadiusStyle}
                    >
                      {keywordBadge}
                    </span>
                  </div>
                )}

                <h1
                  className={cn(
                    "jobsite:mx-auto jobsite:max-w-5xl jobsite:font-heading jobsite:text-4xl jobsite:leading-[1.1] jobsite:tracking-tight jobsite:text-balance jobsite:sm:text-5xl jobsite:md:text-6xl jobsite:xl:text-7xl",
                    headlineTextClass,
                  )}
                >
                  {headline}
                </h1>

                <p
                  className={cn(
                    "jobsite:mx-auto jobsite:max-w-3xl jobsite:text-lg jobsite:leading-relaxed jobsite:sm:text-xl jobsite:md:text-2xl",
                    bodyTextClass,
                  )}
                >
                  {subheadline}
                </p>

                <div className="jobsite:flex jobsite:flex-col jobsite:items-center jobsite:justify-center jobsite:gap-3 jobsite:sm:flex-row">
                  {ctaUrl !== undefined && (
                    <Button asChild size="lg" className={primaryButtonClass}>
                      <SmartLink to={ctaUrl}>{ctaText}</SmartLink>
                    </Button>
                  )}
                  {resolvedSecondaryCtaText && resolvedSecondaryCtaUrl && (
                    <Button asChild variant="outline" size="lg" className={secondaryButtonClass}>
                      <SmartLink to={resolvedSecondaryCtaUrl}>{resolvedSecondaryCtaText}</SmartLink>
                    </Button>
                  )}
                </div>

                {renderTrustIndicators(true)}
                <HeroLogo src={logoImage} alt={logoImageAlt} color={logoImageColor} centered />
              </div>
            </div>
          </div>
          )}
        </section>
      );
    }

    /** The split layout's side card; null when a facts card would have nothing true to say. */
    const renderSideCard = (): React.ReactNode => {
        if (heroForm === "booking") {
          return (
            <BookingWidgetInline
              slug={bookingSlug}
              apiUrl={bookingApiUrl}
              businessName={businessName}
              businessPhone={metadataPhone}
            />
          );
        }
        if (heroForm === "facts") {
          if (!hasFactsCardContent(factsCard)) {
            return null;
          }
          if (isBold) {
            return <HeroFactsCard className={BOLD_CARD_CLASS} content={factsCard} />;
          }
          return <HeroFactsCard className={FACTS_CARD_CLASS} content={factsCard} marks="primary" />;
        }
        return (
          <HeroLeadCaptureCard
            variant={variant}
            hasTrackingPhone={hasTrackingPhone}
            submitLabel={ctaText}
            businessSlug={bookingSlug}
          />
        );
      },
      sideCard = renderSideCard(),
      backgroundLayers = hasText(backgroundImage) ? (
        <>
          <img
            src={backgroundImage}
            alt=""
            crossOrigin="anonymous"
            fetchPriority="high"
            style={imagePositionStyle(imagePosition)}
            className={cn(
              "jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-full jobsite:object-cover jobsite:object-center",
              imageMotionClass(imageMotion),
            )}
            aria-hidden="true"
          />
          {navIsTransparent && (
            <div
              className="jobsite:pointer-events-none jobsite:absolute jobsite:inset-x-0 jobsite:top-0 jobsite:h-32 jobsite:bg-linear-to-b jobsite:from-black/35 jobsite:to-transparent"
              aria-hidden="true"
            />
          )}
          <div
            className="jobsite:absolute jobsite:inset-0"
            style={{ backgroundImage: heroOverlayGradient(boldOverlay, darkness) }}
            aria-hidden="true"
          />
        </>
      ) : null;

    if (isBold) {
      // A dark band sized by its content, not a min-height: on a phone the
      // Buttons sit above the fold and the facts card follows them.
      return (
        <section
          id={id}
          className={cn(
            "jobsite:relative jobsite:overflow-hidden jobsite:pb-16 jobsite:md:pb-24",
            hasText(backgroundImage) ? FRAME_ON_MEDIA_CLASS : "jobsite:bg-primary",
            navIsTransparent ? "jobsite:pt-28 jobsite:md:pt-36" : "jobsite:pt-12 jobsite:md:pt-20",
            className,
          )}
        >
          {backgroundLayers}
          {/* The same container and gutter as the bold sections below, so every left edge lines up. */}
          <div className={cn(BOLD_CONTAINER_CLASS, "jobsite:relative jobsite:z-10")}>
            <div
              className={cn(
                "jobsite:grid jobsite:grid-cols-1 jobsite:gap-10 jobsite:lg:gap-12 jobsite:xl:gap-16",
                sideCard === null
                  ? "jobsite:max-w-5xl"
                  : "jobsite:lg:grid-cols-[minmax(0,1.6fr)_minmax(320px,0.8fr)] jobsite:lg:items-center",
              )}
            >
              <div className="jobsite:@container jobsite:space-y-6">
                {renderBoldEyebrow(false)}
                <h1 className={boldHeadlineClass}>{headline}</h1>
                {subheadline && <p className={boldBodyClass}>{subheadline}</p>}
                <div className="jobsite:pt-2">{renderBoldButtons(false)}</div>
              </div>
              {/* Under the copy on a phone or tablet, the card keeps a card's width rather than the screen's. */}
              {sideCard !== null && <div className="jobsite:relative jobsite:z-10 jobsite:max-w-lg jobsite:lg:max-w-none">{sideCard}</div>}
            </div>
          </div>
        </section>
      );
    }

    return (
      <section
        id={id}
        className={cn(
          "jobsite:relative jobsite:flex jobsite:min-h-[620px] jobsite:items-center jobsite:overflow-hidden jobsite:pb-14 jobsite:md:pb-20 jobsite:xl:min-h-[700px]",
          backgroundImage && FRAME_ON_MEDIA_CLASS,
          navIsTransparent ? "jobsite:pt-30 jobsite:md:pt-36" : "jobsite:pt-14 jobsite:md:pt-20",
          !backgroundImage &&
            "jobsite:bg-linear-to-br jobsite:from-primary jobsite:via-primary jobsite:to-primary/90",
          className,
        )}
      >
        {backgroundImage && (
          <img
            src={backgroundImage}
            alt=""
            crossOrigin="anonymous"
            fetchPriority="high"
            style={imagePositionStyle(imagePosition)}
            className={cn(
              "jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-full jobsite:object-cover jobsite:object-center",
              imageMotionClass(imageMotion),
            )}
            aria-hidden="true"
          />
        )}
        {navIsTransparent && backgroundImage && (
          <div
            className="jobsite:pointer-events-none jobsite:absolute jobsite:inset-x-0 jobsite:top-0 jobsite:h-32 jobsite:bg-linear-to-b jobsite:from-black/35 jobsite:to-transparent"
            aria-hidden="true"
          />
        )}
        {backgroundImage && (
          <div
            className="jobsite:absolute jobsite:inset-0"
            style={{ backgroundImage: heroOverlayGradient(overlay, darkness) }}
            aria-hidden="true"
          />
        )}

        <div className="jobsite:container jobsite:relative jobsite:z-10 jobsite:mx-auto jobsite:w-full jobsite:px-4 jobsite:md:px-6 jobsite:lg:px-8">
          <div
            className={
              sideCard === null
                ? "jobsite:grid jobsite:max-w-4xl jobsite:grid-cols-1 jobsite:items-center jobsite:gap-8"
                : "jobsite:grid jobsite:grid-cols-1 jobsite:items-center jobsite:gap-8 jobsite:lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] jobsite:xl:gap-16"
            }
          >
            <div className="jobsite:space-y-6">
              <div
                className={cn(
                  "jobsite:space-y-5",
                  variant === "panel" &&
                    "jobsite:max-w-2xl jobsite:border jobsite:px-6 jobsite:py-6 jobsite:shadow-2xl jobsite:sm:px-8 jobsite:sm:py-8",
                  variant === "panel" &&
                    (lightText
                      ? "jobsite:border-white/15 jobsite:bg-black/20 jobsite:backdrop-blur-xl"
                      : "jobsite:border-border/80 jobsite:bg-background/94"),
                )}
                style={variant === "panel" ? surfaceRadiusStyle : undefined}
              >
                {keywordBadge && (
                  <span
                    className={cn(
                      "jobsite:inline-flex jobsite:px-4 jobsite:py-1.5 jobsite:text-xs jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.18em] jobsite:shadow-lg",
                      lightText
                        ? "jobsite:border jobsite:border-white/28 jobsite:bg-black/15 jobsite:backdrop-blur-md jobsite:text-white"
                        : "jobsite:border jobsite:border-accent/25 jobsite:bg-accent/15 jobsite:text-accent",
                    )}
                    style={surfaceRadiusStyle}
                  >
                    {keywordBadge}
                  </span>
                )}

                <h1
                  className={cn(
                    "jobsite:max-w-2xl jobsite:font-heading jobsite:text-4xl jobsite:leading-[1.1] jobsite:tracking-tight jobsite:text-balance jobsite:sm:text-5xl jobsite:xl:text-6xl",
                    headlineTextClass,
                  )}
                >
                  {headline}
                </h1>

                <p
                  className={cn(
                    "jobsite:max-w-2xl jobsite:text-lg jobsite:leading-relaxed jobsite:sm:text-xl jobsite:xl:text-2xl",
                    bodyTextClass,
                  )}
                >
                  {subheadline}
                </p>
              </div>

              <div className="jobsite:flex jobsite:flex-col jobsite:gap-3 jobsite:sm:flex-row">
                {ctaUrl !== undefined && (
                  <Button asChild size="lg" className={primaryButtonClass}>
                    <SmartLink to={ctaUrl}>{ctaText}</SmartLink>
                  </Button>
                )}
                {resolvedSecondaryCtaText && resolvedSecondaryCtaUrl && (
                  <Button asChild variant="outline" size="lg" className={secondaryButtonClass}>
                    <SmartLink to={resolvedSecondaryCtaUrl}>{resolvedSecondaryCtaText}</SmartLink>
                  </Button>
                )}
              </div>

              {renderTrustIndicators(false)}
              <HeroLogo src={logoImage} alt={logoImageAlt} color={logoImageColor} centered={false} />
            </div>

            {sideCard !== null && <div className="jobsite:relative jobsite:z-10 jobsite:xl:pl-6">{sideCard}</div>}
          </div>
        </div>
      </section>
    );
  };

export type { HeroVariant, HeroPhoto, HeroProps };
export { Hero };
